import { ChangeEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Box, Card, Container, Typography } from '@material-ui/core';
import { RootState } from 'store';
import { changeContratoEntePrivadoEditingValue, clearCodCoopEntePrivadoGenericForm, clearContratoEntePrivadoEditing } from 'store/modules/ContratosFluxoDescentralizado/EntePrivado/EntePrivadoActions';
import { changeContratoEntePublicoEditingValue, clearCodCoopEntePublicoGenericForm, clearContratoEntePublicoEditing } from 'store/modules/ContratosFluxoDescentralizado/EntePublico/EntePublicoActions';
import { changeContratoAssessorEditingValue, clearCodCoopAssessorGenericForm, clearContratoAssessorEditing } from 'store/modules/ContratosFluxoDescentralizado/Assessores/AssessoresActions';
import { loadCooperativas } from 'store/modules/LogradouroActions';
import { EditValueAssessor } from 'store/modules/ContratosFluxoDescentralizado/Assessores/__types';
import { EditValueEntePublico } from 'store/modules/ContratosFluxoDescentralizado/EntePublico/__types';
import { EditValueEntePrivado } from 'store/modules/ContratosFluxoDescentralizado/EntePrivado/__types';
import { clearCooperativaByCod, loadCooperativaByCod } from 'store/modules/AllContratos/AllContratosActions';
import { tipoContratoRoutes, URL_ENTE_DESCENTRALIZADO } from 'models/contratos';
import { Col } from 'components/Col';
import { Row } from 'components/Row';
import { NewAutoCompleteField } from 'components/NewAutoCompleteField';
import { CustomTextField } from 'components/CustomTextField';
import { CEPMaskedInput, CNPJMaskedInput } from 'components/MaskedInputs';
import { EMAIL_MAX_LENGTH } from 'utils/ValidationUtils';
import useValidations from '../hooks/useValidations';
import '../index.scss'

const FluxoGenericDadosCooperativa = () => {
  const [tipoEnteDescentralizado, setTipoEnteDescentralizado] = useState<string | null>(null)
  const [codCoop, setCodCoop] = useState<string | null>(null)
  const { emailCoopValueIsValid, isContratoAtivoAssessor, isContratoAtivoEntePrivado, validaStatusContratoFluxo, isContratoAtivoEntePublico, codCoopValueIsValid, fieldDisabled } = useValidations();
  const contratoNewAssessorReducer = useSelector((state: RootState) => state.contratosDescentralizadosAssessores)
  const contratoNewEntePublicoReducer = useSelector((state: RootState) => state.contratosDescentralizadosEntePublico);
  const contratoNewEntePrivadoReducer = useSelector((state: RootState) => state.contratosDescentralizadosEntePrivado);
  const contratosReducer = useSelector((state: RootState) => state.contratos);
  const languageValues = useSelector((state: RootState) => state.languages.languageValues);
  const logradouro = useSelector((state: RootState) => state.logradouro);
  const dispatch = useDispatch();
  const location = useLocation();

  const fluxoAssessor = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ASSESSOR)
  const fluxoEntePublico = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PUBLICO)
  const fluxoEntePrivado = location.pathname.includes(URL_ENTE_DESCENTRALIZADO.ENTE_PRIVADO)

  useEffect(() => {
    switch (true) {
      case fluxoAssessor:
        setTipoEnteDescentralizado(tipoContratoRoutes.ASSESSOR);
        break;
      case fluxoEntePublico:
        setTipoEnteDescentralizado(tipoContratoRoutes.ENTE_PUBLICO);
        break;
      case fluxoEntePrivado:
        setTipoEnteDescentralizado(tipoContratoRoutes.ENTE_PRIVADO);
        break;
      default:
        setTipoEnteDescentralizado(null);
        break;
    }
  }, [fluxoAssessor, fluxoEntePublico, fluxoEntePrivado, location.pathname]);

  useEffect(() => {
    dispatch(loadCooperativas(false));
  }, [dispatch]);

  useEffect(() => {
    if (codCoop) {
      dispatch(loadCooperativaByCod(codCoop));
    } else if (logradouro.cooperativas.length === 1) {
      const unicaCooperativa = logradouro.cooperativas[0];
      setValue({
        target: {
          name: 'codCoop',
          value: unicaCooperativa.codigo,
        },
      } as ChangeEvent<HTMLInputElement>);
    }
    return () => {
      dispatch(clearCooperativaByCod());
    };
    // eslint-disable-next-line
  }, [codCoop, logradouro.cooperativas, dispatch]);

  useEffect(() => {
    switch (tipoEnteDescentralizado) {
      case tipoContratoRoutes.ASSESSOR:
        if (contratoNewAssessorReducer.contratoEditing.codCoop) {
          setCodCoop(contratoNewAssessorReducer.contratoEditing.codCoop);
        } else {
          dispatch(clearContratoEntePublicoEditing());
          dispatch(clearContratoEntePrivadoEditing());
          setCodCoop(null);
        }
        break;
      case tipoContratoRoutes.ENTE_PUBLICO:
        if (contratoNewEntePublicoReducer.contratoEditing.codCoop) {
          setCodCoop(contratoNewEntePublicoReducer.contratoEditing.codCoop);
        } else {
          dispatch(clearContratoAssessorEditing());
          dispatch(clearContratoEntePrivadoEditing());
          setCodCoop(null);
        }
        break;
      case tipoContratoRoutes.ENTE_PRIVADO:
        if (contratoNewEntePrivadoReducer.contratoEditing.codCoop) {
          setCodCoop(contratoNewEntePrivadoReducer.contratoEditing.codCoop);
        } else {
          dispatch(clearContratoAssessorEditing());
          dispatch(clearContratoEntePublicoEditing());
          setCodCoop(null);
        }
        break;
      default:
        break;
    }
  }, [contratoNewAssessorReducer.contratoEditing.codCoop,
  contratoNewEntePrivadoReducer.contratoEditing.codCoop,
  contratoNewEntePublicoReducer.contratoEditing.codCoop,
    dispatch,
    tipoEnteDescentralizado]);

  const setValue = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    switch (tipoEnteDescentralizado) {
      case tipoContratoRoutes.ASSESSOR:
        dispatch(changeContratoAssessorEditingValue(name, value));
        break;
      case tipoContratoRoutes.ENTE_PUBLICO:
        dispatch(changeContratoEntePublicoEditingValue(name, value));
        break;
      case tipoContratoRoutes.ENTE_PRIVADO:
        dispatch(changeContratoEntePrivadoEditingValue(name, value));
        break;
      default:
        break;
    }
  };

  const reset = () => {
    switch (tipoEnteDescentralizado) {
      case tipoContratoRoutes.ASSESSOR:
        dispatch(clearCodCoopAssessorGenericForm());
        break;
      case tipoContratoRoutes.ENTE_PUBLICO:
        dispatch(clearCodCoopEntePublicoGenericForm());
        break;
      case tipoContratoRoutes.ENTE_PRIVADO:
        dispatch(clearCodCoopEntePrivadoGenericForm());
        break;
      default:
        break;
    }
  };

  const getFieldValue = (
    field: keyof EditValueAssessor | keyof EditValueEntePublico,
  ) => {
    switch (tipoEnteDescentralizado) {
      case tipoContratoRoutes.ASSESSOR:
        return contratoNewAssessorReducer.contratoEditing[field as keyof EditValueAssessor];
      case tipoContratoRoutes.ENTE_PUBLICO:
        return contratoNewEntePublicoReducer.contratoEditing[field as keyof EditValueEntePublico];
      case tipoContratoRoutes.ENTE_PRIVADO:
        return contratoNewEntePrivadoReducer.contratoEditing[field as keyof EditValueEntePrivado];
      default:
        return '';
    }
  };

  const disableFields = () => {
    if (fluxoAssessor) {
      return !isContratoAtivoAssessor();
    } else if (fluxoEntePublico) {
      return !isContratoAtivoEntePublico();
    } else if (fluxoEntePrivado) {
      return !isContratoAtivoEntePrivado();
    }
  }

  return (
    <>
      <Container className='componentsDescentralizado__container'>
        <Box className='componentsDescentralizado__box'>
          <Typography variant='h5' className='componentsDescentralizado__title'>
            {languageValues.dadosCooperativa}
          </Typography>
          <Typography variant='h6' className='componentsDescentralizado__sub'>
            {languageValues.preenchaDadosCooperativa}
          </Typography>
        </Box>
      </Container>

      <Card className='componentsDescentralizado__card'>
        <Box>
          <form
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <Row>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'email'}
                  label={languageValues.cadastroContratoEmail}
                  name='emailCoop'
                  value={getFieldValue('emailCoop')}
                  onChange={setValue}
                  counter={true}
                  maxSize={EMAIL_MAX_LENGTH}
                  error={!emailCoopValueIsValid().status}
                  helperText={
                    !emailCoopValueIsValid().status
                      ? emailCoopValueIsValid().error
                      : ''
                  }
                  disabled={
                    disableFields() ||
                    !validaStatusContratoFluxo()
                  }
                />
              </Col>
              <Col cols={[12, 12, 6, 6, 6]}>
                <NewAutoCompleteField
                  id={'cooperativas'}
                  label={languageValues.cadastroContratoCooperativa}
                  options={logradouro.cooperativas.map(item => ({
                    label: item.nome,
                    value: item.codigo,
                  }))}
                  value={getFieldValue('codCoop')}
                  onChange={(value: { value: string } | null) => {
                    const currValue = value && value.value ? value.value : '';
                    if (!currValue) {
                      reset();
                    } else {
                      setValue({
                        target: {
                          name: 'codCoop',
                          value: currValue,
                        },
                      } as ChangeEvent<HTMLInputElement>);
                    }
                  }}
                  error={!codCoopValueIsValid().status}
                  helperText={
                    !codCoopValueIsValid().status
                      ? codCoopValueIsValid().error
                      : ''
                  }
                  disabled={
                    disableFields() ||
                    !validaStatusContratoFluxo()
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'cnpj'}
                  label={languageValues.cadastroContratoCNPJ}
                  name='cnpjCoop'
                  value={contratosReducer.cooperativaByCod.cnpj}

                  InputProps={{
                    readOnly: true,
                    inputComponent: CNPJMaskedInput,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'cep'}
                  label={languageValues.cadastroContratoCep}
                  name='cepCoop'
                  value={contratosReducer.cooperativaByCod.cep}
                  InputProps={{
                    readOnly: true,
                    inputComponent: CEPMaskedInput,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'endereco'}
                  label={languageValues.cadastroContratoEndereco}
                  name='enderecoCoop'
                  value={contratosReducer.cooperativaByCod.endereco}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'numero'}
                  label={languageValues.cadastroContratoNumero}
                  name='numeroCoop'
                  value={contratosReducer.cooperativaByCod.numero}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'complemento'}
                  label={languageValues.cadastroContratoComplemento}
                  name='complementoCoop'
                  value={contratosReducer.cooperativaByCod.complemento}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'bairro'}
                  label={languageValues.cadastroContratoBairro}
                  name='bairroCoop'
                  value={contratosReducer.cooperativaByCod.bairro}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
            </Row>
            <Row>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'estado'}
                  label={languageValues.cadastroContratoEstado}
                  name='estadoCoop'
                  value={contratosReducer.cooperativaByCod.uf}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
              <Col cols={[12, 12, 6, 6, 6]}>
                <CustomTextField
                  id={'cidade'}
                  label={languageValues.cadastroContratoCidade}
                  name='cidadeCoop'
                  value={contratosReducer.cooperativaByCod.municipio}
                  InputProps={{
                    readOnly: true,
                  }}
                  disabled={
                    fieldDisabled() ||
                    !codCoopValueIsValid().status ||
                    contratosReducer.loadingCooperativaByCod
                  }
                />
              </Col>
            </Row>
          </form>
        </Box>
      </Card>
    </>
  )
}

export default FluxoGenericDadosCooperativa